import { useEffect, useRef, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SiteLegalFooter } from "@/components/layout/site-legal-footer";
import { toast } from "sonner";

function CustomerSignShell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1">{children}</div>
      <SiteLegalFooter
        openPrivacyInNewTab
        className="sticky bottom-0 border-t border-gray-100 bg-white/95 backdrop-blur-sm"
      />
    </div>
  );
}

function digitsOnly(raw: string, maxLen: number) {
  return raw.replace(/\D/g, "").slice(0, maxLen);
}

function formatPhoneDisplay(digits: string) {
  const d = digits.replace(/\D/g, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatSsnDisplay(digits: string) {
  const d = digits.replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

type Meta = {
  signer_kind: string;
  application_id: number;
  internal_id: string;
  sign_target_label: string;
  signer_email?: string | null;
  identity_mode: "SSN_LAST_FOUR" | "PROFILE_REQUIRED";
  profile_complete: boolean;
  verified: boolean;
  verify_ttl_seconds: number;
  verify_expires_at: string | null;
  completed: boolean;
  pg_upgrade?: boolean;
};

type ApplicationPayload = {
  internal_id?: string;
  company?: { name?: string };
  equipment_name?: string;
  consent_language?: { content?: string };
};

type ProfileForm = {
  email: string;
  phone: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  postal_code: string;
  ssn: string;
  dob: string;
  ownership_percent: string;
};

const emptyProfile: ProfileForm = {
  email: "",
  phone: "",
  address_1: "",
  address_2: "",
  city: "",
  state: "",
  postal_code: "",
  ssn: "",
  dob: "",
  ownership_percent: "",
};

export function CustomerSignPage() {
  const { token } = useParams<{ token: string }>();
  const queryClient = useQueryClient();
  const [lastFour, setLastFour] = useState("");
  const [profileForm, setProfileForm] = useState<ProfileForm>(emptyProfile);
  const [signDone, setSignDone] = useState(false);
  const [showFullTerms, setShowFullTerms] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  const metaQuery = useQuery({
    queryKey: ["customer-sign-meta", token],
    queryFn: async () => {
      const { data } = await customInstance.get<Meta>(`/api/customer-sign/${token}/meta/`);
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  const applicationQuery = useQuery({
    queryKey: ["customer-sign-application", token],
    queryFn: async () => {
      const { data } = await customInstance.get<ApplicationPayload>(
        `/api/customer-sign/${token}/application/`
      );
      return data;
    },
    enabled: !!token && !!metaQuery.data?.verified && !metaQuery.data?.completed,
    retry: false,
  });

  useEffect(() => {
    if (applicationQuery.isError) {
      const status = (applicationQuery.error as any)?.response?.status;
      if (status === 403) {
        toast.error("Your verification session expired. Please verify again.");
        queryClient.invalidateQueries({ queryKey: ["customer-sign-meta", token] });
      }
    }
  }, [applicationQuery.isError, applicationQuery.error, queryClient, token]);

  const verifyMutation = useMutation({
    mutationFn: async (payload: { ssn_last_four?: string; confirm_profile_on_file?: boolean }) => {
      const { data } = await customInstance.post(`/api/customer-sign/${token}/verify/`, payload);
      return data;
    },
    onSuccess: () => {
      toast.success("Verified.");
      queryClient.invalidateQueries({ queryKey: ["customer-sign-meta", token] });
      queryClient.invalidateQueries({ queryKey: ["customer-sign-application", token] });
    },
    onError: (error: any) => {
      const code = error.response?.data?.code;
      if (code === "profile_required") {
        toast.error("Please complete your profile below.");
      } else {
        toast.error(error.response?.data?.error || "Verification failed");
      }
    },
  });

  const profileMutation = useMutation({
    mutationFn: async (vars: { form: ProfileForm; omitEmail: boolean; pgUpgrade: boolean }) => {
      const { form, omitEmail, pgUpgrade } = vars;
      const payload: Record<string, string> = {
        phone: digitsOnly(form.phone, 10),
        address_1: form.address_1.trim(),
        address_2: form.address_2.trim(),
        city: form.city.trim(),
        state: form.state.trim().toUpperCase().slice(0, 2),
        postal_code: form.postal_code.trim(),
        ssn: digitsOnly(form.ssn, 9),
      };
      if (!omitEmail) {
        payload.email = form.email.trim();
      }
      if (pgUpgrade) {
        payload.dob = form.dob.trim();
        payload.ownership_percent = form.ownership_percent.trim();
      }
      const { data } = await customInstance.post(`/api/customer-sign/${token}/profile/`, payload);
      return data;
    },
    onSuccess: () => {
      toast.success("Profile saved. You can continue to sign.");
      queryClient.invalidateQueries({ queryKey: ["customer-sign-meta", token] });
      queryClient.invalidateQueries({ queryKey: ["customer-sign-application", token] });
    },
    onError: (error: any) => {
      const err = error.response?.data;
      if (typeof err === "object" && err !== null) {
        const first = Object.values(err).flat()[0];
        toast.error(typeof first === "string" ? first : "Could not save profile");
      } else {
        toast.error("Could not save profile");
      }
    },
  });

  const signMutation = useMutation({
    mutationFn: async (signatureDataUrl: string) => {
      const { data } = await customInstance.post(`/api/customer-sign/${token}/sign/`, {
        signature: signatureDataUrl,
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Signature recorded. Thank you.");
      setSignDone(true);
      queryClient.invalidateQueries({ queryKey: ["customer-sign-meta", token] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || "Failed to record signature");
    },
  });

  if (!token) {
    return (
      <div className="max-w-lg mx-auto p-8 text-center text-gray-600">Invalid link.</div>
    );
  }

  if (metaQuery.isLoading) {
    return (
      <CustomerSignShell>
        <div className="max-w-lg mx-auto p-12 flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
          <p className="text-gray-600 text-sm">Loading…</p>
        </div>
      </CustomerSignShell>
    );
  }

  if (metaQuery.isError) {
    const status = (metaQuery.error as any)?.response?.status;
    const msg =
      status === 410
        ? "This link is no longer valid."
        : status === 404
          ? "We could not find this signing link."
          : "Unable to load this page.";
    return (
      <CustomerSignShell>
        <div className="max-w-lg mx-auto p-8 text-center space-y-2">
          <p className="text-gray-800 font-medium">{msg}</p>
          <p className="text-sm text-gray-600">Contact your dealer if you need a new link.</p>
        </div>
      </CustomerSignShell>
    );
  }

  const meta = metaQuery.data!;

  if (meta.completed || signDone) {
    return (
      <CustomerSignShell>
        <div className="max-w-xl mx-auto p-8 space-y-4 text-center">
          <img src="/logo-full.png" alt="DealerDog" className="max-h-24 mx-auto object-contain" />
          <h1 className="text-xl font-semibold text-gray-900">You&apos;re all set</h1>
          <p className="text-gray-600 text-sm">
            {meta.signer_kind === "ADDITIONAL_OWNER"
              ? "Your supplemental signature has been recorded."
              : meta.signer_kind === "PG_UPGRADE"
                ? "Your personal guarantor information and signature have been recorded."
                : "Your signature has been recorded."}
          </p>
          <p className="text-xs text-gray-500">Reference: {meta.internal_id}</p>
        </div>
      </CustomerSignShell>
    );
  }

  const isPgUpgrade = meta.pg_upgrade || meta.signer_kind === "PG_UPGRADE";
  const title = isPgUpgrade
    ? "Complete personal guarantor"
    : meta.signer_kind === "ADDITIONAL_OWNER"
      ? "Supplemental signature"
      : "Sign your application";

  const identityMode = meta.identity_mode ?? "SSN_LAST_FOUR";
  const hasPreservedEmail = Boolean(meta.signer_email?.trim());

  const needsProfileGate =
    !meta.verified && identityMode === "PROFILE_REQUIRED" && !meta.profile_complete;

  const needsProfileConfirm =
    !meta.verified && identityMode === "PROFILE_REQUIRED" && meta.profile_complete;

  return (
    <CustomerSignShell>
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="flex justify-center mb-2">
        <img src="/logo-full.png" alt="DealerDog" className="max-h-32 object-contain" />
      </div>
      <h1 className="text-2xl font-bold text-center text-gray-900">{title}</h1>
      <p className="text-center text-sm text-gray-600">
        Signing as <span className="font-medium">{meta.sign_target_label}</span> — Application{" "}
        <span className="font-mono">{meta.internal_id}</span>
      </p>

      {!meta.verified && needsProfileGate && (
        <div className="space-y-4 bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold">
            {isPgUpgrade ? "Personal guarantor information" : "Complete your profile"}
          </h2>
          <p className="text-sm text-gray-600">
            {isPgUpgrade
              ? "To upgrade this corporate-only application to a full personal guarantor application, we need your contact information, date of birth, ownership percentage, and Social Security number."
              : hasPreservedEmail
                ? "We need your phone, address, and Social Security number before you can sign. Your email is already on file with your dealer."
                : "We need your email, phone, address, and Social Security number before you can sign."}
          </p>
          {hasPreservedEmail && (
            <p className="text-xs text-gray-500">
              Email on file: <span className="font-medium text-gray-700">{meta.signer_email}</span>
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-3">
            {!hasPreservedEmail && (
              <div className="sm:col-span-2">
                <Label htmlFor="pf-email">Email</Label>
                <Input
                  id="pf-email"
                  type="email"
                  autoComplete="email"
                  className="mt-1"
                  value={profileForm.email}
                  onChange={(e) => setProfileForm((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
            )}
            <div>
              <Label htmlFor="pf-phone">Mobile phone</Label>
              <Input
                id="pf-phone"
                inputMode="tel"
                autoComplete="tel"
                placeholder="000-000-0000"
                className="mt-1"
                value={formatPhoneDisplay(profileForm.phone)}
                onChange={(e) =>
                  setProfileForm((p) => ({ ...p, phone: digitsOnly(e.target.value, 10) }))
                }
              />
            </div>
            <div>
              <Label htmlFor="pf-ssn">SSN (9 digits)</Label>
              <Input
                id="pf-ssn"
                inputMode="numeric"
                autoComplete="off"
                placeholder="000-00-0000"
                className="mt-1 tracking-widest"
                maxLength={11}
                value={formatSsnDisplay(profileForm.ssn)}
                onChange={(e) =>
                  setProfileForm((p) => ({ ...p, ssn: digitsOnly(e.target.value, 9) }))
                }
              />
            </div>
            {isPgUpgrade && (
              <>
                <div>
                  <Label htmlFor="pf-dob">Date of birth</Label>
                  <Input
                    id="pf-dob"
                    type="date"
                    autoComplete="bday"
                    className="mt-1"
                    value={profileForm.dob}
                    onChange={(e) => setProfileForm((p) => ({ ...p, dob: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="pf-ownership">Ownership %</Label>
                  <Input
                    id="pf-ownership"
                    inputMode="decimal"
                    placeholder="e.g. 100"
                    className="mt-1"
                    value={profileForm.ownership_percent}
                    onChange={(e) =>
                      setProfileForm((p) => ({
                        ...p,
                        ownership_percent: e.target.value.replace(/[^\d.]/g, ""),
                      }))
                    }
                  />
                </div>
              </>
            )}
            <div className="sm:col-span-2">
              <Label htmlFor="pf-a1">Street address</Label>
              <Input
                id="pf-a1"
                autoComplete="street-address"
                className="mt-1"
                value={profileForm.address_1}
                onChange={(e) => setProfileForm((p) => ({ ...p, address_1: e.target.value }))}
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="pf-a2">Apt / suite (optional)</Label>
              <Input
                id="pf-a2"
                className="mt-1"
                value={profileForm.address_2}
                onChange={(e) => setProfileForm((p) => ({ ...p, address_2: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="pf-city">City</Label>
              <Input
                id="pf-city"
                autoComplete="address-level2"
                className="mt-1"
                value={profileForm.city}
                onChange={(e) => setProfileForm((p) => ({ ...p, city: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="pf-state">State</Label>
              <Input
                id="pf-state"
                maxLength={2}
                autoComplete="address-level1"
                className="mt-1 uppercase"
                value={profileForm.state}
                onChange={(e) =>
                  setProfileForm((p) => ({ ...p, state: e.target.value.replace(/[^a-zA-Z]/g, "").toUpperCase() }))
                }
              />
            </div>
            <div>
              <Label htmlFor="pf-zip">ZIP</Label>
              <Input
                id="pf-zip"
                autoComplete="postal-code"
                className="mt-1"
                value={profileForm.postal_code}
                onChange={(e) => setProfileForm((p) => ({ ...p, postal_code: e.target.value }))}
              />
            </div>
          </div>
          <Button
            className="bg-blue-600 text-white"
            disabled={profileMutation.isPending}
            onClick={() => {
              if (!hasPreservedEmail && !profileForm.email.trim()) {
                toast.error("Email is required.");
                return;
              }
              if (isPgUpgrade) {
                if (!profileForm.dob.trim()) {
                  toast.error("Date of birth is required.");
                  return;
                }
                const op = parseFloat(profileForm.ownership_percent);
                if (!profileForm.ownership_percent.trim() || Number.isNaN(op) || op < 0 || op > 100) {
                  toast.error("Enter a valid ownership percentage (0–100).");
                  return;
                }
              }
              profileMutation.mutate({
                form: profileForm,
                omitEmail: hasPreservedEmail,
                pgUpgrade: isPgUpgrade,
              });
            }}
          >
            {profileMutation.isPending ? "Saving…" : "Save and continue"}
          </Button>
        </div>
      )}

      {!meta.verified && needsProfileConfirm && (
        <div className="space-y-4 bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold">Continue</h2>
          <p className="text-sm text-gray-600">
            Your contact information is already on file. Confirm to open the signing page.
          </p>
          <Button
            className="bg-blue-600 text-white"
            disabled={verifyMutation.isPending}
            onClick={() => verifyMutation.mutate({ confirm_profile_on_file: true })}
          >
            {verifyMutation.isPending ? "Continuing…" : "Continue to sign"}
          </Button>
        </div>
      )}

      {!meta.verified && identityMode === "SSN_LAST_FOUR" && (
        <div className="space-y-4 bg-white rounded-xl border p-6">
          <h2 className="text-lg font-semibold">Verify your identity</h2>
          <p className="text-sm text-gray-600">
            Enter the last four digits of your Social Security number on file for this application.
          </p>
          <div className="max-w-xs">
            <Label htmlFor="ssn4">Last 4 of SSN</Label>
            <Input
              id="ssn4"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={4}
              value={lastFour}
              onChange={(e) => setLastFour(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="mt-1 tracking-widest"
            />
          </div>
          <Button
            className="bg-blue-600 text-white"
            disabled={lastFour.length !== 4 || verifyMutation.isPending}
            onClick={() => verifyMutation.mutate({ ssn_last_four: lastFour })}
          >
            {verifyMutation.isPending ? "Verifying…" : "Continue"}
          </Button>
        </div>
      )}

      {meta.verified && (
        <div className="space-y-6">
          {applicationQuery.isLoading && (
            <div className="flex justify-center py-8">
              <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          )}
          {applicationQuery.isError && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-900">
              Could not load application details. Verify again if your session expired.
            </div>
          )}
          {applicationQuery.data && !signMutation.isPending && (
            <div className="space-y-4 bg-white rounded-xl border p-4">
              <div className="rounded-lg bg-gray-50 border p-3 text-sm text-gray-700 space-y-1">
                {applicationQuery.data.company?.name && (
                  <p>
                    <span className="font-medium">Business:</span> {applicationQuery.data.company.name}
                  </p>
                )}
                {applicationQuery.data.equipment_name && (
                  <p>
                    <span className="font-medium">Equipment:</span> {applicationQuery.data.equipment_name}
                  </p>
                )}
              </div>

              <h2 className="text-lg font-semibold pt-2">Your signature</h2>
              <p className="text-sm text-gray-600">Draw your signature in the box below.</p>
              <div className="border rounded-lg overflow-hidden bg-gray-50">
                <canvas
                  ref={canvasRef}
                  width={500}
                  height={200}
                  className="w-full max-w-full touch-none cursor-crosshair border-b border-gray-200"
                  style={{ touchAction: "none" }}
                  onPointerDown={(e) => {
                    const canvas = canvasRef.current;
                    if (!canvas) return;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) return;
                    ctx.strokeStyle = "#000";
                    ctx.lineWidth = 2;
                    ctx.lineCap = "round";
                    ctx.lineJoin = "round";
                    isDrawingRef.current = true;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const x = (e.clientX - rect.left) * scaleX;
                    const y = (e.clientY - rect.top) * scaleY;
                    ctx.beginPath();
                    ctx.moveTo(x, y);
                  }}
                  onPointerMove={(e) => {
                    if (!isDrawingRef.current) return;
                    const canvas = canvasRef.current;
                    if (!canvas) return;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) return;
                    const rect = canvas.getBoundingClientRect();
                    const scaleX = canvas.width / rect.width;
                    const scaleY = canvas.height / rect.height;
                    const x = (e.clientX - rect.left) * scaleX;
                    const y = (e.clientY - rect.top) * scaleY;
                    ctx.lineTo(x, y);
                    ctx.stroke();
                  }}
                  onPointerUp={() => {
                    isDrawingRef.current = false;
                  }}
                  onPointerLeave={() => {
                    isDrawingRef.current = false;
                  }}
                />
              </div>
              <p className="text-xs text-gray-600">
                Review the terms below, then click <span className="font-medium">Submit signature</span> to agree to
                them.
              </p>

              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const canvas = canvasRef.current;
                    if (canvas) {
                      const ctx = canvas.getContext("2d");
                      if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
                    }
                  }}
                >
                  Clear
                </Button>
                <Button
                  className="bg-blue-600 text-white"
                  disabled={signMutation.isPending}
                  onClick={() => {
                    const canvas = canvasRef.current;
                    if (!canvas) {
                      toast.error("Signature area not ready.");
                      return;
                    }
                    const dataUrl = canvas.toDataURL("image/png");
                    const ctx = canvas.getContext("2d");
                    if (ctx) {
                      const pixelData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
                      const hasStroke = pixelData.some((_, i) => i % 4 === 3 && pixelData[i] > 0);
                      if (!hasStroke) {
                        toast.error("Please draw your signature first.");
                        return;
                      }
                    }
                    signMutation.mutate(dataUrl);
                  }}
                >
                  {signMutation.isPending ? "Submitting…" : "Submit signature"}
                </Button>
              </div>

              <div className="space-y-2 pt-4">
                <h2 className="text-lg font-semibold">Terms and consent</h2>
                <div
                  className={`text-xs text-gray-600 whitespace-pre-line border rounded-md p-3 bg-gray-50/80 ${
                    !showFullTerms ? "line-clamp-4 sm:line-clamp-none" : ""
                  }`}
                >
                  {applicationQuery.data?.consent_language?.content || ""}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFullTerms(!showFullTerms)}
                  className="text-xs sm:hidden text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                >
                  {showFullTerms ? "Show less" : "Show more"}
                </Button>
              </div>
            </div>
          )}
          {signMutation.isPending && (
            <div className="min-h-[200px] flex flex-col items-center justify-center gap-4 bg-white rounded-xl border p-8 text-center">
              <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
              <p className="text-sm text-gray-600">Saving your signature…</p>
            </div>
          )}
        </div>
      )}
    </div>
    </CustomerSignShell>
  );
}
